/**
 * PATH: packages/email-hub/src/feature/reader-header/runtime/multiTabSync.ts
 * FILE_ID: EMAILHUB-READERHEADER-RUNTIME-MULTITAB-001
 * PURPOSE: Cross-tab coordination for reader placement/workflow mutations.
 *   After a sibling tab mutates the same email, this tab triggers the host's
 *   ONE reconcile callback so both tabs converge (plan "stale multi-tab
 *   overwrite" glitch kill, change #269 P5).
 * ROLE IN ARCHITECTURE: Leaf runtime utility under the reader-header module.
 * MAIN EXPORTS: publishReaderMutation, subscribeReaderMutations,
 *   useReaderMutationSync, READER_SYNC_CHANNEL.
 * NON-RESPONSIBILITIES: No refetching of its own — the host's reconcile
 *   callback owns cache repair. No conflict resolution (version tokens in
 *   placementModel.ts own detection).
 * NOTES FOR FUTURE AI: Transport is BroadcastChannel when available, else a
 *   localStorage `storage` event fallback (fires only in OTHER tabs, so no
 *   self-echo filtering is needed on either path — BroadcastChannel also
 *   never echoes to its posting channel instance). Keep messages tiny and
 *   content-free: emailId + kind only, never payloads or names.
 */
import { useEffect } from 'react'

export const READER_SYNC_CHANNEL = 'qt-reader-header-sync'

export type ReaderMutationKind = 'placements' | 'workflow'

export interface ReaderMutationMessage {
  emailId: string
  kind: ReaderMutationKind
}

function parseMessage(raw: unknown): ReaderMutationMessage | null {
  if (typeof raw !== 'object' || raw === null) return null
  const candidate = raw as Record<string, unknown>
  if (typeof candidate.emailId !== 'string' || candidate.emailId === '') {
    return null
  }
  if (candidate.kind !== 'placements' && candidate.kind !== 'workflow') {
    return null
  }
  return { emailId: candidate.emailId, kind: candidate.kind }
}

function hasBroadcastChannel(): boolean {
  return typeof BroadcastChannel === 'function'
}

/**
 * Announce a settled placement/workflow mutation to sibling tabs.
 *
 * Inputs: the email id and the mutation family. Output: none. Side effects:
 * one BroadcastChannel post (or one localStorage write on the fallback path).
 * Failure mode: any transport error is swallowed — sync is best-effort and
 * must never break the mutation that already succeeded.
 */
export function publishReaderMutation(
  emailId: string,
  kind: ReaderMutationKind,
): void {
  const message: ReaderMutationMessage = { emailId, kind }
  try {
    if (hasBroadcastChannel()) {
      const channel = new BroadcastChannel(READER_SYNC_CHANNEL)
      channel.postMessage(message)
      channel.close()
      return
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      // The nonce forces a distinct value each publish so repeated identical
      // mutations still fire the storage event in sibling tabs.
      window.localStorage.setItem(
        READER_SYNC_CHANNEL,
        JSON.stringify({ ...message, nonce: Date.now() + Math.random() }),
      )
    }
  } catch {
    // Best-effort only.
  }
}

/**
 * Listen for sibling-tab mutations of ONE email. Returns an unsubscribe
 * function. Messages for other emails are ignored.
 */
export function subscribeReaderMutations(
  emailId: string,
  onSibling: (message: ReaderMutationMessage) => void,
): () => void {
  const deliver = (raw: unknown) => {
    const message = parseMessage(raw)
    if (message && message.emailId === emailId) onSibling(message)
  }

  if (hasBroadcastChannel()) {
    const channel = new BroadcastChannel(READER_SYNC_CHANNEL)
    const onMessage = (event: MessageEvent) => deliver(event.data)
    channel.addEventListener('message', onMessage)
    return () => {
      channel.removeEventListener('message', onMessage)
      channel.close()
    }
  }

  if (typeof window === 'undefined') return () => undefined
  const onStorage = (event: StorageEvent) => {
    if (event.key !== READER_SYNC_CHANNEL || !event.newValue) return
    try {
      deliver(JSON.parse(event.newValue))
    } catch {
      // Malformed foreign value — ignore.
    }
  }
  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}

/**
 * Hook wiring: while mounted, a sibling-tab mutation for `emailId` invokes
 * `onSibling` (the host's single reconcile callback). `onSibling` is kept in
 * a stable subscription via re-subscribe on identity change — hosts pass the
 * same memoized reconcile they give the action runner.
 */
export function useReaderMutationSync(
  emailId: string,
  onSibling: (() => void) | undefined,
): void {
  useEffect(() => {
    if (!emailId || !onSibling) return undefined
    return subscribeReaderMutations(emailId, () => onSibling())
  }, [emailId, onSibling])
}
